import { computed, inject, Injectable, signal } from '@angular/core';
import { ENVIRONMENT } from '@ld.consumer-portal/models';

export type EnvironmentFeature =
  | 'SEDM'
  | 'DefaultPayment'
  | 'ServisBot'
  | 'SavvyCard'
  | 'PropertyCard'
  | 'PayoffQuoteRequest'
  | 'MobileAppDemo'
  | 'Autopay'
  | 'AutopayConsent'
  | 'HelocAutopay'
  | 'HelocInHousePayment'
  | 'HelocEligibleLoans';

export interface FeatureConfig {
  enabled: boolean;
  config?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class EnvironmentFeatureService {
  private readonly env = inject(ENVIRONMENT);

  private readonly _overrides = signal<Map<EnvironmentFeature, boolean>>(new Map());

  readonly sedmEnabled = computed(() => this.isEnabled('SEDM'));

  readonly defaultPaymentEnabled = computed(() => this.isEnabled('DefaultPayment'));

  readonly servisBotEnabled = computed(() => this.isEnabled('ServisBot'));

  readonly savvyCardEnabled = computed(() => this.isEnabled('SavvyCard'));

  readonly propertyCardEnabled = computed(() => this.isEnabled('PropertyCard'));

  readonly payoffQuoteEnabled = computed(() => this.isEnabled('PayoffQuoteRequest'));

  readonly mobileAppDemoEnabled = computed(() => this.isEnabled('MobileAppDemo'));

  readonly autopayEnabled = computed(() => this.isEnabled('Autopay'));
  readonly autopayConsentEnabled = computed(() => this.isEnabled('AutopayConsent'));
  readonly helocAutopayEnabled = computed(() => this.isEnabled('HelocAutopay'));
  readonly helocInHousePaymentEnabled = computed(() => this.isEnabled('HelocInHousePayment'));
  readonly helocEligibleLoansEnabled = computed(() => this.isEnabled('HelocEligibleLoans'));

  isEnabled(feature: EnvironmentFeature): boolean {
    const override = this._overrides().get(feature);
    if (override !== undefined) {
      return override;
    }

    return this.getFeatureFromEnv(feature);
  }

  getFeatureConfig(feature: EnvironmentFeature): FeatureConfig {
    const enabled = this.isEnabled(feature);
    const config = this.getFeatureConfigData(feature);

    return { enabled, config };
  }

  getEnabledFeatures(): EnvironmentFeature[] {
    const allFeatures: EnvironmentFeature[] = [
      'SEDM',
      'DefaultPayment',
      'ServisBot',
      'SavvyCard',
      'PropertyCard',
      'PayoffQuoteRequest',
      'MobileAppDemo',
    ];

    return allFeatures.filter((f) => this.isEnabled(f));
  }

  setOverride(feature: EnvironmentFeature, enabled: boolean): void {
    this._overrides.update((overrides) => {
      const updated = new Map(overrides);
      updated.set(feature, enabled);
      return updated;
    });
  }

  clearOverride(feature: EnvironmentFeature): void {
    this._overrides.update((overrides) => {
      const updated = new Map(overrides);
      updated.delete(feature);
      return updated;
    });
  }

  clearAllOverrides(): void {
    this._overrides.set(new Map());
  }

  private getFeatureFromEnv(feature: EnvironmentFeature): boolean {
    const appSettings = this.env.appSettings;

    switch (feature) {
      case 'SEDM':
        return this.parseBooleanConfig(appSettings.enableSEDM);

      case 'DefaultPayment':
        return this.parseBooleanConfig(appSettings.enableDefaultPayment);

      case 'ServisBot':
        return this.parseBooleanConfig(appSettings.AppSettings?.enableServisBot);

      case 'SavvyCard':
        return this.parseBooleanConfig(appSettings.showSaavyCard);

      case 'PropertyCard':
        return this.parseBooleanConfig(appSettings.showPropertyCard);

      case 'PayoffQuoteRequest':
        return this.parseBooleanConfig(appSettings.showPayoffQuoteRequest);

      case 'MobileAppDemo':
        return this.parseBooleanConfig(appSettings.Mobile_App_Demo);

      case 'Autopay':
        return this.parseBooleanConfig((appSettings as Record<string, unknown>)['enableAutopay'] ?? true);

      case 'AutopayConsent':
        return this.parseBooleanConfig((appSettings as Record<string, unknown>)['enableAutopayConsent']);

      case 'HelocAutopay':
        return this.parseBooleanConfig(appSettings.enableHelocAutopay);

      case 'HelocInHousePayment':
        return this.parseBooleanConfig(appSettings.enableHelocInHousePayment);

      case 'HelocEligibleLoans':
        return !!appSettings.AppSettings?.HelocLoans;

      default:
        return false;
    }
  }

  private getFeatureConfigData(feature: EnvironmentFeature): Record<string, unknown> | undefined {
    const appSettings = this.env.appSettings;

    switch (feature) {
      case 'SEDM':
        return {
          documentList: appSettings.SEDMDocumentList,
          downloadDocument: appSettings.SEDMDownloadDocument,
          documentView: appSettings.SEDMDocumentView,
          monthRange: appSettings.SEDMMonthRange,
          documentType: appSettings.SEDMDocumentType,
          monthlyStatementMonth: appSettings.SEDMMonthlyStatementMonth,
        };

      case 'ServisBot':
        return appSettings.servisBot
          ? {
              env: appSettings.servisBot.env,
              endpoint: appSettings.servisBot.endpoint,
              envRegion: appSettings.servisBot.envRegion,
              customStyle: appSettings.servisBot.customStyle,
            }
          : undefined;

      case 'SavvyCard':
        return {
          privacyPolicyLink: appSettings.savvyMoneyPrivacyPolicyLink,
        };

      case 'PropertyCard':
        return {
          comeHomePrivacyPolicyLink: appSettings.comeHomePrivacyPolicyLink,
          houseCanaryLicenseInfoLink: appSettings.houseCanaryLicenseInfoLink,
        };

      default:
        return undefined;
    }
  }

  private parseBooleanConfig(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    return false;
  }
}
